import SelectViewingGroups from "@components/auto-completes/viewing-groups";
import {
  SHDivider,
  SHLabel,
  SHStack
} from "@components/design-systems";
import { SHDatePicker } from "@components/design-systems/sh-date-picker";
import { DateFormat } from "@constants/format";
import { useInputDotIndicator } from "@hooks/useInvestmentProduct";
import { HighlightType } from "@models/configuration";
import { InvestmentProductConfigurationFeature } from "@models/product/investment-product/entities/investmentProduct";
import { InvestmentProductSeriesDTO } from "@models/product/investment-product/entities/series";
import { InvestmentDataStatus } from "@models/product/investment-product/enums/status";
import { ViewingGroupSelectionDTO } from "@models/viewing-groups/entities/viewingGroups";
import { CustomLabel } from "@pages/suppliers/_id/_products/investment-product/_id/edit/components/custom-label";
import { SeriesPath } from "@pages/suppliers/_id/_products/investment-product/_id/edit/components/tabs/series";
import {
  getFeatureDataPath,
  getShowHideNoRoleFieldStatus,
} from "@pages/suppliers/_id/_products/investment-product/_id/edit/components/tabs/series/utils";
import { EditProductDefaults } from "@pages/suppliers/_id/_products/investment-product/_id/edit/constants";
import { format, lastDayOfYear, parse } from "date-fns";
import { debounce, isEmpty, isEqual, isNil } from "lodash";
import { useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

interface SeriesFeatureDateTimeProps {
  feature: InvestmentProductConfigurationFeature;
  investmentProductSeriesData: InvestmentProductSeriesDTO;
  selectedIndex: [number, number];
  isDisabled?: boolean;
}

const SeriesFeatureDateTime = ({
  feature,
  investmentProductSeriesData,
  selectedIndex = [0, 0],
  isDisabled = false,
}: SeriesFeatureDateTimeProps) => {
  const { control, setValue, getValues } = useFormContext<{
    investmentProductSeriesData: InvestmentProductSeriesDTO[];
  }>();
  const {
    investmentSeriesDataModifiedPath,
    sectionModifiedPath,
    featureModifiedPath,
    featureProductDataPath,
    featureProductHighlightTypePath,
    featureProductDataStatusPath,
    featureProductDataPublishedPath,
    featureProductDataModifiedPath,
    investmentSeriesDataHighlightTypePath,
    featureViewingGroupSelectionsPath,
    dropDownFeaturePath,
  } = getFeatureDataPath(selectedIndex, investmentProductSeriesData, feature);
  const watchedValue = useWatch({
    control,
    name: featureProductDataPath,
  });
  const productDataValue = useMemo(
    () => getValues(featureProductDataPath),
    // eslint-disable-next-line
    [watchedValue, selectedIndex],
  ) as string | null;
  const { isShowOrangeDot, isShowBlueDot } = useInputDotIndicator<{
    investmentProductSeriesData: InvestmentProductSeriesDTO[];
  }>({
    getValues,
    control,
    paths: {
      highlightType: featureProductHighlightTypePath as SeriesPath,
      dataStatus: featureProductDataStatusPath as SeriesPath,
    },
  });

  const handleVisibilityChange = (
    newValue: ViewingGroupSelectionDTO[] | null,
  ) => {
    const isModified = !isEqual(feature.viewingGroupSelections, newValue);
    setValue(investmentSeriesDataModifiedPath, isModified);
    setValue(sectionModifiedPath, isModified);
    setValue(featureModifiedPath, isModified);
    setValue(featureProductDataModifiedPath, isModified);
  };
  const debounceVisibilityChange = debounce(
    handleVisibilityChange,
    EditProductDefaults.DebounceTime,
  );
  const handleInputChange = (inputDateValue: Date | null) => {
    const publishedData = getValues(featureProductDataPublishedPath) ?? "";
    const inputDateStr = inputDateValue
      ? format(inputDateValue ?? new Date(), DateFormat)
      : "";
    const highlightType = !isEqual(inputDateStr, publishedData)
      ? HighlightType.Edited
      : null;
    const dataStatus =
      isNil(inputDateStr) || isEmpty(inputDateStr)
        ? InvestmentDataStatus.MissingData
        : InvestmentDataStatus.Filled;
    const isModified = !isEqual(inputDateStr, feature.productData?.value);

    setValue(investmentSeriesDataHighlightTypePath, highlightType);
    setValue(featureProductHighlightTypePath, highlightType);
    setValue(featureProductDataStatusPath, dataStatus);
    setValue(featureProductDataModifiedPath, isModified);
    setValue(featureModifiedPath, isModified);
    setValue(sectionModifiedPath, true);
    setValue(investmentSeriesDataModifiedPath, true);
    setValue(featureProductDataPath, inputDateStr);
  };

  if (
    !investmentProductSeriesData ||
    getShowHideNoRoleFieldStatus(getValues, dropDownFeaturePath)
  ) {
    return <></>;
  }

  return (
    <>
      <SHStack
        key={featureProductDataPath}
        direction={"row"}
        alignItems="center"
        justifyContent={"flex-start"}
        gap={2.5}
        sx={{
          pt: "10px",
          pb: "5px",
          wordBreak: "break-word",
        }}
      >
        <SHStack width={"38%"}>
          <Controller
            name={featureProductDataPath as any}
            control={control}
            render={() => (
              <SHDatePicker
                label={
                  <SHLabel
                    label={
                      <CustomLabel
                        featureData={feature}
                        dotProps={{
                          orangeDot: isShowOrangeDot,
                          blueDot: isShowBlueDot,
                        }}
                      />
                    }
                  />
                }
                openOnClick
                onChange={handleInputChange}
                maxDate={lastDayOfYear(new Date())}
                minDate={new Date("1/1/2000")}
                views={["year", "month", "day"]}
                inputFormat="dd/MM/yy"
                disabled={isDisabled}
                value={
                  Boolean(productDataValue)
                    ? parse(productDataValue as string, DateFormat, new Date())
                    : null
                }
                sxTextField={{
                  "&>.MuiInput-root>input": {
                    padding: "8px 5px 5px 5px",
                  },
                }}
              />
            )}
          />
        </SHStack>
        {feature.allowedVisibilitySetting && (
          <SHStack width={"30%"}>
            <Controller
              name={featureViewingGroupSelectionsPath as SeriesPath}
              control={control}
              render={({ field: { ref, ...other } }) => (
                <SelectViewingGroups
                  textFieldProps={{
                    label: `Visible to`,
                  }}
                  onChange={(value) => {
                    other.onChange(value);
                    debounceVisibilityChange(value);
                  }}
                  value={
                    (getValues(
                      featureViewingGroupSelectionsPath as SeriesPath,
                    ) as ViewingGroupSelectionDTO[] | null) || []
                  }
                  viewingGroupData={feature.viewingGroupSelections ?? []}
                  disabled={isDisabled}
                />
              )}
            />
          </SHStack>
        )}
      </SHStack>
      <SHDivider />
    </>
  );
};

export default SeriesFeatureDateTime;
